import React, { ChangeEvent } from 'react';
import format from 'date-fns/format';
import subMonths from 'date-fns/subMonths';
import isEqual from 'react-fast-compare';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { parse } from 'query-string';
import compareDesc from 'date-fns/compareDesc';
import { formatDate, SUPPORTED_DATE_LOCALES } from '../../../../../shared/LanguageService';
import { handleError } from '../../../../../shared/ErrorCatching';
import * as Features from '../../../../../shared/Features';
import * as actions from '../../../OrdersActions';
import { IOrderListItem, IOrderType } from '../../../Entities';
import ListSkeleton from './ListSkeleton';
import NoSearchResults from './NoSearchResults';
import FilterOptions, {
    OrderTypeFilterOptions,
    BrokerAgentFilterOptions,
    FreeTextFilterOptions,
    OrderPlacedAtFilterOptions
} from './FilterOptions';
import { IOrderCollection } from './OrderCollectionList';
import OrderListSection from './OrderListSection';
import OrderCollectionSection from './OrderCollectionSection';
import {
    filterOrderCollectionOnOrderPlacedAtDate,
    filterOrdersOnBrokerAgent,
    filterOrdersOnFreeTextSearch,
    filterOrdersOnOrderType,
    filterOrdersOnPlacedAtDate,
    transformOrderCollectionToOrders
} from './utils/filterOrdersUtils';
import { useTelemetryContext } from '../../../../../telemetry/telemetryContext';
import { Logger } from '../../../../../telemetry/loggers/Logger';
import { getUserInfo } from '../../../../../telemetry/utils';
import { findEarliestAvailableOrderPlacedAtDate } from './utils/orderDateUtils';

export type OrderCollectionsWithDate = {
    date: Date;
    title: string;
    collections: IOrderCollection[];
};

export type OrdersWithTitle = {
    title: string;
    orders: IOrderListItem[];
};

type OrdersPageState = {
    filter: string;
    orders: IOrderListItem[];
    selectedOrderType: number;
    selectedBrokerAgent: number;
    availableOrderTypes: IOrderType[];
    orderCreatedAtStart: Date | null;
    orderCreatedAtEnd: Date | null;
    isLoading: boolean;
};

type ReduxProps = {
    orderCollections: IOrderCollection[];
    brokerAgents: any[];
    user: any;
};

type ReduxActions = {
    actions: {
        getOrders: () => void;
    };
};

type OrdersPageProps = {
    logger: Logger;
} & ReduxProps &
    ReduxActions &
    RouteComponentProps;

const LIST_UPDATE_INTERVAL_MS: number = 600000;
const MONTH_ORDER_COLLECTIONS_KEY_DATE_FORMAT = 'yyyy-MM';
const MONTH_HEADING_DATE_FORMAT = 'LLLL yyyy';

function compareDateDesc(first, second) {
    return compareDesc(first.date, second.date);
}

class OrdersPage extends React.Component<OrdersPageProps, OrdersPageState> {
    private pollingTimer: number;
    private monthlyOrderCollections: null | Map<string, IOrderCollection[]> = null;

    constructor(props: OrdersPageProps) {
        super(props);

        const todaysDate = new Date(Date.now());

        this.state = {
            filter: '',
            orders: [],
            selectedOrderType: -1,
            selectedBrokerAgent: -1,
            availableOrderTypes: [],
            orderPlacedAtStart: subMonths(todaysDate, 2),
            orderPlacedAtEnd: todaysDate,
            isLoading: false
        };

        this.closeModal = this.closeModal.bind(this);
        this.navigateTo = this.navigateTo.bind(this);
    }

    componentDidMount(): void {
        let params = parse(this.props.location.search);
        let orderId = params.orderId;
        this.setState({ newOrderId: orderId });

        this._newOrderIdTimeout = setTimeout(() => {
            this.setState({ newOrderId: null });
        }, 5000);

        this.pollForOrders().catch(handleError);
    }

    componentWillUnmount() {
        clearTimeout(this.pollingTimer);
    }

    pollForOrders = async (): Promise<void> => {
        try {
            await this.getOrders();
            clearTimeout(this.pollingTimer);
            this.pollingTimer = window.setTimeout(this.pollForOrders, LIST_UPDATE_INTERVAL_MS);

        } catch (error) {
            throw error;
        }
    };

    updateAvailableOrderTypes = () => {
        const { orderCollections } = this.props;
        const availableOrderTypes = orderCollections.reduce((acc, orderCollection) => {
            orderCollection.orders.forEach(({ orderType }) => {
                acc.set(orderType.value, orderType);
            });
            return acc;
        }, new Map<number, IOrderType>());

        const availableOrderTypesArray: IOrderType[] = [];
        for (const [, orderType] of availableOrderTypes) {
            availableOrderTypesArray.push(orderType);
        }

        this.setState({ availableOrderTypes: availableOrderTypesArray });
    };

    updateMonthlyOrderCollections = () => {
        const { orderCollections } = this.props;
        this.monthlyOrderCollections = orderCollections.reduce((acc, orderCollection) => {
            const month = format(
                new Date(orderCollection.lastOrderPlacedAt),
                MONTH_ORDER_COLLECTIONS_KEY_DATE_FORMAT,
                {
                    locale: SUPPORTED_DATE_LOCALES.en
                }
            );
            const entry: IOrderCollection[] | undefined = acc.get(month);

            if (!entry) {
                acc.set(month, [orderCollection]);
            } else {
                acc.set(month, entry.concat([orderCollection]));
            }

            return acc;
        }, new Map<string, IOrderCollection[]>());
    };

    getOrders = (): Promise<void> => {
        if (this.state.isLoading) {
            return Promise.resolve();
        }

        this.setState({ isLoading: true });
        return this.props.actions
            .getOrders()
            .then(() => {
                this.updateAvailableOrderTypes();
                this.updateMonthlyOrderCollections();
                this.setState({ isLoading: false });
            })
            .catch((error: any) => {
                this.setState({ isLoading: false });
                throw error;
            });
    };

    shouldShowAllOrderTypes = (): boolean => {
        return !(this.state.selectedOrderType > -1);
    };

    getOrderCollectionsByMonth = (): OrderCollectionsWithDate[] => {
        const orderCollectionsByMonth: OrderCollectionsWithDate[] = [];
        if (this.monthlyOrderCollections) {
            const possibleErrors = [];

            for (const [date, collections] of this.monthlyOrderCollections) {
                try {
                    const dateObj = new Date(date);

                    orderCollectionsByMonth.push({
                        date: dateObj,
                        title: formatDate(dateObj, MONTH_HEADING_DATE_FORMAT),
                        collections
                    });
                } catch (error) {
                    possibleErrors.push({ date, error });
                }
            }

            if (possibleErrors.length > 0) {
                const { logger, user } = this.props;
                const error = possibleErrors[0].error;

                const additionalInfo = {
                    origin: 'OrdersPage.getOrderCollectionsByMonth',
                    userInfo: getUserInfo(user),
                    datesWithError: possibleErrors.map(({ date }) => date),
                    dateKeys: Array.from(this.monthlyOrderCollections.keys())
                };
                logger.logException(error, additionalInfo);
            }

            orderCollectionsByMonth.sort(compareDateDesc);
        }

        return orderCollectionsByMonth;
    };

    getOrderTypeFilterOptions = (): OrderTypeFilterOptions => {
        const { availableOrderTypes, selectedOrderType } = this.state;

        return {
            availableOrderTypes: availableOrderTypes,
            orderType: selectedOrderType,
            onChangeOrderType: (event: ChangeEvent<HTMLSelectElement>) => {
                this.setState({ selectedOrderType: Number(event.target.value) });
            }
        };
    };

    getBrokerAgentFilterOptions = (): BrokerAgentFilterOptions | null => {
        const { brokerAgents, user } = this.props;
        const { selectedBrokerAgent } = this.state;

        if (!Features.hasFeature(Features.FilterOrderListOnBrokerAgent, user)) {
            return null;
        }

        return {
            availableBrokerAgents: brokerAgents,
            brokerAgent: selectedBrokerAgent,
            onChangeBrokerAgent: (event: ChangeEvent<HTMLSelectElement>) => {
                this.setState({
                    selectedBrokerAgent: Number(event.target.value)
                });
            }
        };
    };

    getFreeTextFilterOptions = (): FreeTextFilterOptions => {
        return {
            searchPhrase: this.state.filter ? this.state.filter : '',
            onChangeFreeText: (event: ChangeEvent<HTMLInputElement>) => {
                this.setState({ filter: event.target.value });
            }
        };
    };

    getOrderPlacedAtFilterOptions = (earliestAvailableDate: Date): OrderPlacedAtFilterOptions => {
        const { orderPlacedAtStart, orderPlacedAtEnd } = this.state;

        return {
            earliestAvailableDate,
            orderPlacedAtStart,
            orderPlacedAtEnd,
            onOrderPlacedAtStartChange: (newDate: Date) => {
                this.setState({ orderPlacedAtStart: newDate });
            },
            onOrderPlacedAtEndChange: (newDate: Date) => {
                this.setState({ orderPlacedAtEnd: newDate });
            }
        };
    };

    sortOrderCollectionsByDateDesc = (firstOrderCollection, secondOrderCollection) => {
        return compareDesc(
            new Date(firstOrderCollection.lastOrderPlacedAt),
            new Date(secondOrderCollection.lastOrderPlacedAt)
        );
    };

    sortOrdersByDateDesc = (firstOrder: IOrderListItem, secondOrder: IOrderListItem): number => {
        return compareDesc(new Date(firstOrder.orderPlacedAt), new Date(secondOrder.orderPlacedAt));
    };

    filterOrderCollectionsOnOrderType = (orderCollection: IOrderCollection): boolean => {
        const { selectedOrderType } = this.state;

        if (selectedOrderType === -1) {
            return true;
        }

        return orderCollection.orders.some(filterOrdersOnOrderType(selectedOrderType));
    };

    filterOrderCollectionsOnBrokerAgent = (orderCollection: IOrderCollection): boolean => {
        const { selectedBrokerAgent } = this.state;

        if (selectedBrokerAgent === 0) {
            return true;
        }

        return orderCollection.orders.some(filterOrdersOnBrokerAgent(selectedBrokerAgent));
    };

    filterOrderCollectionsOnFreeTextSearch = (orderCollection: IOrderCollection): boolean => {
        if (!this.state.filter || this.state.filter === '') {
            return true;
        }

        const freeTextSearch = this.state.filter.toLowerCase();

        let hasMatches = false;

        const orderNumbers = orderCollection.orderNumberRange.split('-');
        const fullOrderNumbers = orderNumbers.map(
            (orderNumber) => `${orderCollection.orderCollectionNumber}:${orderNumber}`
        );
        if (
            `${orderCollection.orderCollectionNumber}`.includes(freeTextSearch) ||
            fullOrderNumbers.some((fullOrderNumber) => fullOrderNumber.includes(freeTextSearch))
        ) {
            hasMatches = true;
        }

        const name = orderCollection.name ? orderCollection.name.toLowerCase() : '';
        if (name.includes(freeTextSearch)) {
            hasMatches = true;
        }

        const city = orderCollection.city ? orderCollection.city.toLowerCase() : '';
        if (city.includes(freeTextSearch)) {
            hasMatches = true;
        }

        hasMatches =
            hasMatches || orderCollection.orders.some(filterOrdersOnFreeTextSearch(freeTextSearch));

        return hasMatches;
    };

    render() {
        const {
            orderPlacedAtStart,
            selectedOrderType,
            orderPlacedAtEnd,
            filter,
            selectedBrokerAgent
        } = this.state;
        const { orderCollections } = this.props;
        const earliestAvailableDate = findEarliestAvailableOrderPlacedAtDate(orderCollections);

        const orderCollectionsByMonth = this.getOrderCollectionsByMonth();
        const filteredOrderCollections = orderCollectionsByMonth.filter(({ collections }) => {
            const res = collections
                .filter(this.filterOrderCollectionsOnOrderType)
                .filter(this.filterOrderCollectionsOnBrokerAgent)
                .filter(this.filterOrderCollectionsOnFreeTextSearch)
                .filter(
                    filterOrderCollectionOnOrderPlacedAtDate(
                        orderPlacedAtStart,
                        orderPlacedAtEnd,
                        earliestAvailableDate
                    )
                );
            return res.length > 0;
        });

        const orderTypeFilterOptions = this.getOrderTypeFilterOptions();
        const brokerAgentFilterOptions = this.getBrokerAgentFilterOptions();
        const freeTextFilterOptions = this.getFreeTextFilterOptions();
        const orderPlacedAtFilterOptions =
            this.getOrderPlacedAtFilterOptions(earliestAvailableDate);

        if (this.shouldShowAllOrderTypes()) {
            const collectionListOfOrders = filteredOrderCollections.map(
                ({ title, collections }) => {
                    const filteredCollections = collections
                        .filter(this.filterOrderCollectionsOnBrokerAgent)
                        .filter(this.filterOrderCollectionsOnFreeTextSearch)
                        .filter(
                            filterOrderCollectionOnOrderPlacedAtDate(
                                orderPlacedAtStart,
                                orderPlacedAtEnd,
                                earliestAvailableDate
                            )
                        )
                        .sort(this.sortOrderCollectionsByDateDesc)
                        .map((collection) => {
                            const orders = collection.orders
                                .filter(filterOrdersOnBrokerAgent(selectedBrokerAgent))
                                .filter(filterOrdersOnFreeTextSearch(filter))
                                .filter(
                                    filterOrdersOnPlacedAtDate(
                                        orderPlacedAtStart,
                                        orderPlacedAtEnd,
                                        earliestAvailableDate
                                    )
                                )
                                .sort(this.sortOrdersByDateDesc);
                            return {
                                ...collection,
                                orders
                            };
                        });

                    return {
                        title,
                        collections: filteredCollections
                    };
                }
            );

            return (
                <div>
                    <FilterOptions
                        orderTypeFilterOptions={orderTypeFilterOptions}
                        brokerAgentFilterOptions={brokerAgentFilterOptions}
                        freeTextFilterOptions={freeTextFilterOptions}
                        orderPlacedAtFilterOptions={orderPlacedAtFilterOptions}
                    />

                    {this.state.isLoading ? (
                        <ListSkeleton />
                    ) : collectionListOfOrders.length > 0 ? (
                        collectionListOfOrders.map(({ title, collections }) => {
                            return (
                                <OrderCollectionSection
                                    key={title}
                                    title={title}
                                    orderCollections={collections}
                                    user={this.props.user}
                                    navigateTo={this.navigateTo}
                                    newOrderId={this.state.newOrderId}
                                />
                            );
                        })
                    ) : (
                        <NoSearchResults />
                    )}
                </div>
            );
        }

        const collectionOfOrders = filteredOrderCollections
            .map(transformOrderCollectionToOrders)
            .map(({ title, orders }) => {
                return {
                    title,
                    orders: orders
                        .filter(filterOrdersOnOrderType(selectedOrderType))
                        .filter(filterOrdersOnBrokerAgent(selectedBrokerAgent))
                        .filter(filterOrdersOnFreeTextSearch(filter))
                        .filter(
                            filterOrdersOnPlacedAtDate(
                                orderPlacedAtStart,
                                orderPlacedAtEnd,
                                earliestAvailableDate
                            )
                        )
                        .sort(this.sortOrdersByDateDesc)
                };
            });

        return (
            <div>
                <FilterOptions
                    orderTypeFilterOptions={orderTypeFilterOptions}
                    brokerAgentFilterOptions={brokerAgentFilterOptions}
                    freeTextFilterOptions={freeTextFilterOptions}
                    orderPlacedAtFilterOptions={orderPlacedAtFilterOptions}
                />

                {this.state.isLoading ? (
                    <ListSkeleton />
                ) : collectionOfOrders.length > 0 ? (
                    collectionOfOrders.map(({ title, orders }) => (
                        <OrderListSection
                            key={title}
                            title={title}
                            orders={orders}
                            user={this.props.user}
                            navigateTo={this.navigateTo}
                            newOrderId={this.state.newOrderId}
                        />
                    ))
                ) : (
                    <NoSearchResults />
                )}
            </div>
        );
    }

    closeModal() {
        this.setState({ showModal: false, selectedOrderId: null });
    }

    navigateTo(route: string) {
        this.props.history.push(route);
    }
}

function OrdersPageWithLogger(props) {
    const { logger } = useTelemetryContext();
    return <OrdersPage {...props} logger={logger} />;
}

function mapStateToProps(state: any, ownProps: any) {
    return {
        orderCollections: state.orders.orderCollections,
        brokerAgents: state.orders.brokerAgents,
        user: state.user
    };
}

function mapDispatchToProps(dispatch: any) {
    return {
        actions: {
            getOrders: () => dispatch(actions.getOrders())
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(OrdersPageWithLogger));
